import React, { useState, useEffect, ChangeEvent } from 'react';
import Modal from 'react-modal';
import './ReportGenerator.css';
import { Oval } from 'react-loader-spinner';
import switchers from '../assets/switchers.jpg';
import upgraders from '../assets/upgraders.jpg';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';

interface ReportGeneratorProps {
  isOpen: boolean;
  onRequestClose: () => void;
}

const ReportGenerator: React.FC<ReportGeneratorProps> = ({ isOpen, onRequestClose }) => {
  const [template, setTemplate] = useState<string>('Switchers');
  const [jobId, setJobId] = useState<string | null>(null);
  const [exampleUrl, setExampleUrl] = useState<string | undefined>(undefined);
  const [jobStatus, setJobStatus] = useState<string | null>('');
  const getJobId = () => jobId? jobId : '';
  const [startDate, setStartDate] = useState<string>("2024-01-01");
  const [endDate, setEndDate] = useState<string>("2024-01-31");
  const [month, setMonth] = useState<string | undefined>("January");
  const[monthType, setMonthType] = useState<string>("Monthly");
  const TODAY_DATE = new Date();

  const handleTemplateChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setTemplate(event.target.value);
  };

  const handleMonthlyChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setMonthType(event.target.value);
  };

  const handleMonthChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setMonth(event.target.value);
  };

  // The following useEffects ensure that the state variables are updated instantly when the variables change
  useEffect(() => {
    if (month === "January") {
      setStartDate("2024-01-01");
      setEndDate("2024-01-31");
    } else if (month === "February") {
      setStartDate("2024-02-01");
      setEndDate("2024-02-28");
    } else if (month === "March") {
      setStartDate("2024-03-01");
      setEndDate("2024-03-31");
    } else if (month === "April") {
      setStartDate("2024-04-01");
      setEndDate("2024-04-30");
    } else if (month === "May") {
      setStartDate("2024-05-01");
      setEndDate("2024-05-31");
    } else if (month === "June") {
      setStartDate("2024-06-01");
      setEndDate("2024-06-30");
    } else if (month === "July") {
      setStartDate("2024-07-01");
      setEndDate("2024-07-31");
    } else if (month === "August") {
      setStartDate("2024-08-01");
      setEndDate("2024-08-31");
    } else if (month === "September") {
      setStartDate("2024-09-01");
      setEndDate("2024-09-30");
    } else if (month === "October") {
      setStartDate("2024-10-01");
      setEndDate("2024-10-31");
    } else if (month === "November") {
      setStartDate("2024-11-01");
      setEndDate("2024-11-30");
    } else if (month === "December") {
      setStartDate("2024-12-01");
      setEndDate("2024-12-31");
    }

    if (monthType === "Year to Date") {
      setStartDate("2024-01-01");
    }
  }, [month, monthType]);

  useEffect(() => {
  }, [template]);

  const handleGenerate = async () => {
    if (template === "Upgraders") {
      return alert('The Upgraders report is undergoing changes. Currently only the Switchers report is available.');
    }
    else if (new Date(startDate) > TODAY_DATE || new Date(endDate) > TODAY_DATE) {
      return alert('Please select a month before today\'s date.');
    }
    else if (getJobId()) {
      return alert('Cannot generate more than one report at once.');
    }
    else {
      callAPIProcessReport();     
    }
  };

  async function callAPIProcessReport() {
    const secretKey = process.env.REACT_APP_API_KEY;
    try {
      const uuid = uuidv4();

      const queryParams = new URLSearchParams({
        jobId: uuid,
        startDate: startDate,
        endDate: endDate,
        template: template
      }).toString();

      // Append query parameters to the path
      const path = `https://nwszo7z8wh.execute-api.us-east-1.amazonaws.com/processReport?${queryParams}`;

      const response = await axios.put(path, {}, {
        headers: {
          Authorization: `Bearer ${secretKey}`,
        }
      });

      console.log('Response:', response.data);
      setJobId(response.data.job_id);

    } catch (error) {
      console.error('Error calling AWS endpoint:', error);
    }
  }

  const callAPIPollReport = async () => {
      if (!jobId) return;  
      const secretKey = process.env.REACT_APP_API_KEY;
  
      try {  
         // Define query parameters
        const queryParams = new URLSearchParams({
           jobId: getJobId()
        }).toString();
  
        // Append query parameters to the path
        const path = `https://nwszo7z8wh.execute-api.us-east-1.amazonaws.com/pollReport?${queryParams}`;
        console.log("PATH", path)
        const response = await axios.get(path, {
          headers: {
            Authorization: `Bearer ${secretKey}`,
          }
        });
        console.log('Response:', response.data);

        setJobStatus(response.data.status);

        // If url is empty but the job succeeded, that means there is no data availabe (glue job returned empty url)
        if (response.data.url === ''){
          setJobStatus('FAILED')
        } else {
          setExampleUrl(response.data.url);
        }
        
        console.log('GET call succeeded: ', response.data);
        if (jobStatus === 'SUCCEEDED' || jobStatus === 'FAILED') {
           setJobId(null)
        }
      } catch (error) {
          console.log('GET call failed: ', error);
          setJobId(null)
      }
    };

  useEffect(() => {
      if (jobId) {
          const interval = setInterval(callAPIPollReport, 5000);
          return () => clearInterval(interval);
      }
  }, [jobId]);

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      className="report-modal"
      overlayClassName="report-modal-overlay"
    >
      <div className="modal-header">
        <h2>Report Generator</h2>
        <button onClick={onRequestClose} className="close-button-report">&times;</button>
      </div>
      <p style={{marginTop:"0px", marginBottom:"20px"}}>Generate a report with most up to date metrics/KPIs</p>
      <div className="modal-content">
        <div className="form-section">
          <h3 style={{marginBottom:"20px"}}>Configure Your Report</h3>
          <label>
            <span>Pillar</span>
            <select className='category-select-template' value={template} onChange={handleTemplateChange}>
                <option>iPhone</option>
            </select>
          </label>
          <label>
            <span>Template</span>
            <select className='category-select-template' value={template} onChange={handleTemplateChange}>
                <option>Switchers</option>
                <option>Upgraders</option>
            </select>
          </label>
          <label>
            <span>Market</span>
            <select className='category-select-template' value={template} onChange={handleTemplateChange}>
                <option>Global</option>
            </select>
          </label>
          <label>
            <div style={{display: 'flex', flexDirection: 'row'}}>
              <span>Date Range</span>
              <span className="info-icon" title="'Monthly' will select data for the selected month, while 'Year to Date' will select data from the beginning of the year to the end of the month selected.">ℹ️</span>
            </div>
            <select className='category-select-template' value={monthType} onChange={handleMonthlyChange}>
                <option>Monthly</option>
                <option>Year to Date</option>
            </select>
          </label>
          <label>
            <span>Month</span>
            <select className='category-select-template' value={month} onChange={handleMonthChange}>
                <option>January</option>
                <option>February</option>
                <option>March</option>
                <option>April</option>
                <option>May</option>
                <option>June</option>
                <option>July</option>
                <option>August</option>
                <option>September</option>
                <option>October</option>
                <option>November</option>
                <option>December</option>
            </select>
          </label>
          <div className='report-buttons'>
            {/* FIX: disable generate button if jobId exists*/}
            <button onClick={handleGenerate} className="report-button">Generate</button>
          </div>
        </div>
        <div className="preview-section">
          {!jobId && <p style={{marginBottom: "5px", marginTop:"0px", fontWeight:"bold"}} >Preview</p>}
          {!jobId && <div style={{borderWidth: "1px", borderStyle:"solid", borderColor:"gray", padding:"25px"}}>
            {template === 'Switchers' && <img src={switchers} alt="switchers" className="template-preview-img " />}
            {template === 'Upgraders' && <img src={upgraders} alt="upgraders" className="template-preview-img " />}
          </div>}
          {jobId && jobStatus != 'FAILED' && jobStatus != 'SUCCEEDED' && <Oval visible={true} height="70" width="70" color="grey" secondaryColor='grey' ariaLabel="oval-loading" wrapperStyle={{}} wrapperClass="" />}
          <div>
            {jobStatus === '' && <p>Choose your configuration to generate your report</p>}
            {jobStatus === 'RUNNING' && <p>Job is running. This may take a few minutes.</p>}
            {jobStatus === 'SUCCEEDED' && <p>Job completed! <a href={exampleUrl} target="_blank" rel="noopener noreferrer">View Result</a></p>}
            {jobStatus === 'FAILED' && <p>Job failed. Data is not available for selected date range.</p>}
        </div>
        </div>
      </div>
    </Modal>
  );
};

export default ReportGenerator;